/**
 * WizardProposal - generic container for displaying and acting on proposals.
 *
 * Takes a render prop for type-specific rendering of the proposal data.
 * Shows Accept and Reject buttons.
 */

import { Check, X, Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'

interface WizardProposalProps {
  proposal: Record<string, unknown>
  onAccept: () => void
  onReject: () => void
  isAccepting?: boolean
  isRejecting?: boolean
  renderProposal: (proposal: Record<string, unknown>) => React.ReactNode
}

export function WizardProposal({
  proposal,
  onAccept,
  onReject,
  isAccepting = false,
  isRejecting = false,
  renderProposal,
}: WizardProposalProps) {
  const busy = isAccepting || isRejecting

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 border-b-[0.5px] border-b-[var(--rule)] px-5 py-4">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
          Proposed Configuration
        </h2>
      </div>
      {/* Proposal content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {renderProposal(proposal)}
      </div>
      {/* Actions */}
      <div className="flex shrink-0 gap-3 border-t-[0.5px] border-t-[var(--rule)] px-5 py-4">
        <Button
          onClick={onAccept}
          disabled={busy}
          className="flex h-9 flex-1 items-center justify-center gap-2 rounded-[4px] bg-[var(--green)] text-[12px] font-medium tracking-[0.04em] text-[var(--bg)] transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60 hover:opacity-90"
        >
          {isAccepting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          Accept & Create
        </Button>
        <Button
          variant="outline"
          onClick={onReject}
          disabled={busy}
          className="flex h-9 items-center justify-center gap-2 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-4 text-[12px] text-[var(--ink-2)] transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60 hover:bg-[var(--bg-subtle)]"
        >
          {isRejecting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
          Revise
        </Button>
      </div>
    </div>
  );
}
